import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm, SubmitHandler } from "react-hook-form";
import { usePostData } from "../../../../hooks";
import { EndPoints } from "../../../../Constants/endpoints";
import { GeneralButton, GeneralInput, Modal } from "../../../../components";
import { useToast } from "../../../../Services/ToastContext";

interface AddPatientProps {
  onClose: () => void;
  isReportModalOpen: boolean;
  Id: number;
}

function AddReport({ onClose, isReportModalOpen, Id }: AddPatientProps) {
  const { t } = useTranslation();

  const ReportSchema = z.object({
    Description: z
      .string()
      .min(1, t("required_field"))
      .refine((value) => value.trim().length > 0, {
        message: t("required_field"),
      }),
  });

  type AddReportFormInputs = z.infer<typeof ReportSchema>;
  interface ReportPayload extends AddReportFormInputs {
    DefendantId: number;
  }

  const methods = useForm<AddReportFormInputs>({
    resolver: zodResolver(ReportSchema),
    defaultValues: {
      Description: "",
    },
  });

  const { handleSubmit, reset } = methods;
  const { showToast } = useToast();
  const { mutate } = usePostData(`${EndPoints.AddReport}`);

  const onSubmit: SubmitHandler<AddReportFormInputs> = (data) => {
    const payload: ReportPayload = {
      Description: data.Description,
      DefendantId: Id,
    };

    mutate(payload, {
      onSuccess: () => {
        reset();
        onClose();
        showToast(t("report_sent_successfully"), "success");
      },
      onError: (error: any) => {
        showToast(error.message || t("failed_to_add_patient"), "error");
      },
    });
  };

  useEffect(() => {
    if (!isReportModalOpen) {
      reset();
    }
  }, [isReportModalOpen, reset]);

  return (
    <Modal
      header={t("report_patient")}
      isOpen={isReportModalOpen}
      onClose={onClose}
      customStyle="max-w-[500px]"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <GeneralInput
            type="textarea"
            name="Description"
            label={t("report")}
            placeholder={t("report")}
            maxLength={250}
          />
          <div className="mt-5 flex justify-end gap-2">
            <GeneralButton
              fontColor="body-text"
              title={t("cancel")}
              color="lite"
              onClick={() => {
                onClose();
                reset();
              }}
            />
            <GeneralButton type="submit" title={t("send_report")} color="red" />
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}

export default AddReport;
